import { uniq } from "lodash";
import {
  AP_ACTION_TYPE,
  type ApActionType,
  type ApAgentAction,
  type ApAgentRun,
  type ApInvoiceStatus,
} from "@/accounts-payable/types";
import { formatApDate, HIGH_CONFIDENCE } from "./format";
import { needsJob } from "./job-matching";

export const isPendingReviewAction = (action: ApAgentAction) =>
  action.status === "pending_review";

export const getPrerequisiteActions = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
): ApAgentAction[] =>
  (action.prerequisite_action_ids ?? []).flatMap((id) =>
    runActions.filter((a) => a.id === id),
  );

export const getNewItemsToCreate = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
) => getPrerequisiteActions(action, runActions).filter(isPendingReviewAction);

export const isApprovedWithPendingAction = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
) =>
  runActions.some(
    (a) =>
      isPendingReviewAction(a) &&
      !a.approval_blocked_reason &&
      (a.prerequisite_action_ids ?? []).includes(action.id),
  );

export const approveLabel = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
): string => {
  if (action.approval_blocked_reason) return "Approve";

  const count = getNewItemsToCreate(action, runActions).length;
  return count > 0
    ? `Approve + create ${count} item${count === 1 ? "" : "s"}`
    : "Approve";
};

export const isHighConfidenceApprovable = (
  action: ApAgentAction,
  runActions: ApAgentAction[],
) =>
  isPendingReviewAction(action) &&
  !isNoOpAction(action) &&
  action.confidence >= HIGH_CONFIDENCE &&
  !action.approval_blocked_reason &&
  getNewItemsToCreate(action, runActions).every(
    (item) => item.confidence >= HIGH_CONFIDENCE,
  );

export const highConfidenceActionIds = (
  items: {
    last_completed_agent_run: { actions: ApAgentAction[] };
    status?: ApInvoiceStatus;
    st_job_external_id?: number | null;
    st_project_external_id?: number | null;
  }[],
  actionTypes: readonly ApActionType[],
) =>
  uniq(
    items
      .filter(({ status, st_job_external_id, st_project_external_id }) =>
        status
          ? !needsJob({ status, st_job_external_id, st_project_external_id })
          : true,
      )
      .flatMap(({ last_completed_agent_run: { actions } }) =>
        actions
          .filter(
            (action) =>
              (actionTypes.length === 0 ||
                actionTypes.includes(action.action_type)) &&
              isHighConfidenceApprovable(action, actions),
          )
          .flatMap((action) => [
            ...getNewItemsToCreate(action, actions).map((item) => item.id),
            action.id,
          ]),
      ),
  );

export const isNoOpAction = (action: ApAgentAction) =>
  action.action_type === AP_ACTION_TYPE.flagForReview ||
  action.action_type === AP_ACTION_TYPE.noAction;

export type ApActionProvenance = "undecided" | "human" | "automation";

export const actionProvenance = (action: ApAgentAction): ApActionProvenance => {
  if (isPendingReviewAction(action)) return "undecided";
  return action.reviewed_by === null ? "automation" : "human";
};

export const isAutomatedRun = (run: ApAgentRun) =>
  run.automation_qualification === "qualified_selected";

const QBO_APPLY_FAILED: Partial<Record<ApActionType, string>> = {
  [AP_ACTION_TYPE.createQboBill]: "Couldn't be created in QuickBooks",
  [AP_ACTION_TYPE.updateQboBill]: "Couldn't be updated in QuickBooks",
};

export const automatedOutcomeCopy = (action: ApAgentAction): string | null => {
  if (action.rollback_status === "rolled_back")
    return `Reversed on ${formatApDate(action.rolled_back_at)}`;
  if (action.rollback_status === "rollback_failed")
    return "Couldn't be reversed";
  if (action.status === "apply_failed") {
    const failed =
      QBO_APPLY_FAILED[action.action_type] ??
      "Couldn't be applied in ServiceTitan";
    return action.failure_reason
      ? `${failed}: ${action.failure_reason}`
      : failed;
  }
  if (action.status === "rejected") return "Rejected";
  if (action.status === "superseded") return "Superseded by a newer run";
  if (action.status === "approved" && action.waiting_reason)
    return action.waiting_reason;
  if (isWaitingOnEarlierAction(action))
    return "Waiting on an earlier action on this PO";
  if (action.status === "approved" || action.status === "applying")
    return "Applying…";
  return null;
};

export const isWaitingOnEarlierAction = (action: ApAgentAction) =>
  action.status === "approved" &&
  (action.waiting_on_action_ids?.length ?? 0) > 0;

const decidedBy = (action: ApAgentAction): string =>
  actionProvenance(action) === "automation"
    ? "automatically"
    : `by ${action.reviewed_by?.trim() || "a teammate"}`;

export const actionOutcomeCopy = (action: ApAgentAction): string | null => {
  if (isPendingReviewAction(action)) return null;

  // Before the no-op branch: a superseded flag was never seen or ignored.
  if (action.status === "superseded") return "Superseded by a newer run";

  if (isNoOpAction(action))
    return action.status === "rejected"
      ? `Ignored ${decidedBy(action)}`
      : `Marked as seen ${decidedBy(action)}`;
  if (action.status === "rejected") return `Rejected ${decidedBy(action)}`;
  return automatedOutcomeCopy(action);
};

export const getBulkApproveIds = (actions: ApAgentAction[]) =>
  actions
    .filter(
      (action) =>
        isPendingReviewAction(action) && !action.approval_blocked_reason,
    )
    .map((action) => action.id);

export const isBulkApprovable = (action: ApAgentAction) =>
  isPendingReviewAction(action) &&
  !isNoOpAction(action) &&
  !action.approval_blocked_reason;
